import CustomTooltip from '../../../../shared/CustomTooltip';
import React, { useEffect, useState } from 'react';
import { Close, Refresh, ZoomIn, ZoomOut } from '@mui/icons-material';
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Popover,
    Slider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import { useCreateProfilePictureMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../../layout/styledcomponents/CustomIconButton';
import toast from '../../../../shared/Toast';
import { Media } from '../../../../types/user';
import { Area, usePhotoEditor } from './usePhotoEditor';
import { usePhotoValidation } from './usePhotoValidation';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: Media) => void;
    photo?: File;
}

const ProfilePictureModal = ({
    open,
    onClose,
    onSubmit,
    photo,
}: ModalProps) => {
    const [upload, setUpload] = useState<File>();

    const { croppedPhoto, previewUrl, originalUrl, handleCrop, handleEdit } =
        usePhotoEditor(upload);

    const { rejectionReasons, isValidating, validationToken } =
        usePhotoValidation(croppedPhoto);

    const [createProfilePicture] = useCreateProfilePictureMutation();

    const [editing, setEditing] = useState(false);

    const handleConfirm = async () => {
        await handleEdit();
        setEditing(false);
    };

    const handleSubmit = async () => {
        try {
            if (!croppedPhoto || !validationToken) return;

            const data = new FormData();
            data.append('file', croppedPhoto);

            const media = await createProfilePicture({
                data,
                token: validationToken,
            }).unwrap();
            onSubmit(media);

            onClose();
        } catch (_error) {
            toast.error('Error uploading profile picture');
        }
    };

    const handleClose = () => {
        setEditing(false);
        onClose();
    };

    useEffect(() => {
        if (!photo) return;
        setUpload(photo);
    }, [photo]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
            <DialogTitle>
                Preview
                <IconButton
                    onClick={handleClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {editing ? (
                    <PhotoEditor
                        image={originalUrl.current}
                        handleCrop={handleCrop}
                    />
                ) : (
                    <PhotoPreview
                        url={previewUrl.current}
                        loading={isValidating}
                        errors={rejectionReasons}
                    />
                )}
            </DialogContent>
            <DialogActions>
                {editing ? (
                    <CustomButton
                        color='neutral'
                        variant='contained'
                        // onClick={handleClose}
                        onClick={() => setEditing(false)}
                    >
                        Cancel
                    </CustomButton>
                ) : (
                    <CustomButton
                        variant='outlined'
                        onClick={() => setEditing(true)}
                    >
                        Edit Photo
                    </CustomButton>
                )}
                {editing ? (
                    <CustomButton
                        color='primary'
                        variant='contained'
                        onClick={handleConfirm}
                    >
                        Confirm
                    </CustomButton>
                ) : rejectionReasons.length > 0 ? (
                    <CustomButton
                        color='primary'
                        variant='contained'
                        component='label'
                        sx={{ ml: 1 }}
                    >
                        Choose new photo
                        <input
                            type='file'
                            max={1}
                            accept='image/png, image/jpeg, image/jpg'
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (!e.target.files?.[0]) return;
                                setUpload(e.target.files[0]);
                            }}
                        />
                    </CustomButton>
                ) : (
                    <CustomButton
                        color='primary'
                        variant='contained'
                        onClick={handleSubmit}
                    >
                        Confirm
                    </CustomButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

const PhotoEditor = ({
    image,
    handleCrop,
}: {
    image: string;
    handleCrop: (area: Area) => void;
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });

    const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
        handleCrop(croppedAreaPixels);
    };

    const handleRefresh = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    return (
        <>
            <Box sx={{ position: 'relative', height: 400 }}>
                <Cropper
                    cropShape='round'
                    image={image}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                    }}
                >
                    <Paper
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        {!isMobile && (
                            <>
                                <ZoomOut fontSize='small' sx={{ mx: 1 }} />
                                <Slider
                                    value={zoom}
                                    min={1}
                                    step={0.005}
                                    max={3}
                                    onChange={(_, value) =>
                                        setZoom(value as number)
                                    }
                                    sx={{
                                        color: theme.palette.textAccent.main,
                                        minWidth: 100,
                                    }}
                                />
                                <ZoomIn fontSize='small' sx={{ mx: 1 }} />
                            </>
                        )}
                        <CustomTooltip title='Reset crop'>
                            <CustomIconButton
                                color='primary'
                                onClick={handleRefresh}
                            >
                                <Refresh />
                            </CustomIconButton>
                        </CustomTooltip>
                    </Paper>
                </Box>
            </Box>
            <Alert severity='info' sx={{ m: 2 }}>
                Please ensure that your photo meets the following criteria:
                <ul style={{ paddingLeft: '1.5rem' }}>
                    <li>
                        You’re facing forward and are the only person in your
                        photo.
                    </li>
                    <li>
                        Your face is well-lit, not blurry and fills the frame.
                    </li>
                </ul>
            </Alert>
        </>
    );
};

const PhotoPreview = ({
    url,
    loading,
    errors,
}: {
    url: string;
    loading: boolean;
    errors: string[];
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 2,
                    my: { xs: 16, sm: 0 },
                }}
            >
                <img
                    src={url}
                    style={{
                        borderRadius: '100%',
                        aspectRatio: 1,
                        width: '100%',
                        objectFit: 'cover',
                    }}
                />
            </Box>
            <Stack spacing={1} sx={{ mt: 2 }}>
                {loading && (
                    <Alert
                        severity='info'
                        icon={<CircularProgress size='1rem' />}
                        variant='standard'
                    >
                        Validating
                    </Alert>
                )}
                {errors.length > 0 &&
                    errors.map((error) => (
                        <Alert key={error} severity='error'>
                            {error}
                        </Alert>
                    ))}
                {errors.length === 0 && !loading && (
                    <Alert severity='success'>You're ready to go!</Alert>
                )}
                <Typography
                    variant='body3'
                    sx={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    Why do we need to check this?
                </Typography>
                <Popover
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: {
                            p: 2,
                            maxWidth: 300,
                            borderRadius: 2,
                        },
                    }}
                >
                    <Stack spacing={1}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                            Identity verification
                        </Typography>
                        <Typography
                            variant='body3'
                            sx={{ color: 'text.secondary' }}
                        >
                            Your photo is used for biometric access to some
                            properties on 7DAY. A clear, solo photo of your face
                            ensures you can be accurately identified on-site by{' '}
                            <strong>Nirovision</strong> facial recognition
                            technology.
                        </Typography>
                    </Stack>
                </Popover>
            </Stack>
        </>
    );
};

export default ProfilePictureModal;
